// Import necessary libraries and components
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ConfigProvider, DatePicker, message } from 'antd';
import esES from 'antd/es/locale/es_ES';
import dayjs from 'dayjs';
import 'dayjs/locale/es'; // Import Spanish locale for dayjs
import { TextField, Grid, Card, CardContent, Typography, Box, Container, Tooltip, IconButton, useMediaQuery, useTheme } from '@mui/material';
import Swal from 'sweetalert2';
import CheckroomIcon from '@mui/icons-material/Checkroom'; // For Dress Name
import TagIcon from '@mui/icons-material/Tag'; // For Code
import PersonIcon from '@mui/icons-material/Person'; // For Client
import EventIcon from '@mui/icons-material/Event'; // For Rental Date
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn'; // For Return Date
import PaidIcon from '@mui/icons-material/Paid'; // For Payment status
import MoneyOffIcon from '@mui/icons-material/MoneyOff'; // For Payment status
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'; // For Inventory status
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import Avatar from '@mui/material/Avatar';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import LoadingInformation from '../../Components/LoadingInformation';

dayjs.locale('es'); // Set the locale to Spanish (Dominican Republic)


function DressReturnProcessor() {
	// Define state variables using useState hook
	const [pendingItems, setPendingItems] = useState([]);
	const [search, setSearch] = useState("");
	const [reqResponse, setReqResponse] = useState([]);
	const [dateRange, setDateRange] = useState(null);
	const [loading, setLoading] = useState(true);
	// Control para inicializar solo una vez desde la URL
	const [initializedFromURL, setInitializedFromURL] = useState(false);
	const [messageApi, contextHolder] = message.useMessage();

	// Estados para configuración de impresora
	const [printerConfig, setPrinterConfig] = useState({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
	const [configLoading, setConfigLoading] = useState(false);

	// Estados para extensión
	const [extensionInstalled, setExtensionInstalled] = useState(false);
	const [extensionChecked, setExtensionChecked] = useState(false);

	// Responsive
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.down('md'));

	const navigate = useNavigate();
	const location = useLocation();

	// Constantes para impresión térmica
	const isPortrait = isMobile || isTablet;

	// Function to generate a range of numbers from start to end
	const getPendingItems = async () => {
		const token = localStorage.getItem('token');
		if (!token) {
			setLoading(false);
			messageApi.error({
				content: 'No se encontró el token de autenticación. Por favor, inicie sesión nuevamente.',
				duration: 2,
				className: 'custom-class',
				style: {
					marginTop: '20vh',
					backgroundColor: '#f0f0f0',
					color: '#000',
					fontSize: '16px',
					textAlign: 'center',
				},
			});
			return;
		}
		try {
			const response = await fetch('https://dprincess-training.servehttp.com/pending-items/dresses/pendiente',
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${token}`
					}
				}
			);
			if (!response.ok) {
				return await handleRetryRequest(getPendingItems, "Pending Items")
			}
			const data = await response.json();

			setPendingItems(data);
			setReqResponse(data);
			setLoading(false);
		} catch (error) {
			setLoading(false);
			console.error("Error fetching pending items:", error);
			messageApi.error({
				content: 'Error fetching pending items, please refresh the page',
				duration: 2,
				className: 'custom-class',
				style: {
					marginTop: '20vh',
					backgroundColor: '#f0f0f0',
					color: '#000',
					fontSize: '16px',
					textAlign: 'center',
				},
			});
		}
	}

	// Function to fetch printer configuration from C# agent
	const fetchAgentPrinterConfiguration = async () => {
		if (isPortrait) return; // No hacer nada si es móvil
		setConfigLoading(true);
		const isInstalled = await checkExtensionRealTime();
		if (!isInstalled) {
			console.log('Extensión no instalada para fetchAgentPrinterConfiguration');
			messageApi.error('La extensión "Proxy Impresora Térmica Local" no está instalada. Instálala para obtener configuración de impresora.');
			setConfigLoading(false);
			return;
		}

		try {
			console.log('Enviando proxyRequest para /printer-config via extensión');
			const response = await sendToExtension({
				action: 'proxyRequest',
				path: '/printer-config',
				method: 'GET',
				headers: { 'Content-Type': 'application/json' }
			});

			if (response && response.success) {
				try {
					let config;
					if (typeof response.data === 'string') {
						config = JSON.parse(response.data);
					} else {
						config = response.data;
					}
					console.log("Agent Printer Configuration via extension:", config);
					setPrinterConfig(config);
					if (config.IsPrinterSelected) {
						messageApi.success(`Configuración de impresora '${config.SelectedPrinterName}' (${config.SuggestedCharsPerLine} car.) obtenida.`);
					} else {
						messageApi.info(`Agente no tiene impresora predeterminada. Usando ancho ${config.SuggestedCharsPerLine} car.`);
					}
				} catch (parseError) {
					console.error('Error al parsear configuración de impresora:', parseError);
					messageApi.error('Error al procesar la configuración de impresora recibida.');
					setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
				}
			} else {
				const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
				console.error(`Failed to get printer config via extension: ${errorMsg}`);
				messageApi.error("Fallo al obtener configuración de impresora del agente via extensión.");
				setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
			}
		} catch (error) {
			console.error("Error en fetchAgentPrinterConfiguration via extensión:", error);
			messageApi.error("Error de conexión al contactar agente para configuración de impresora via extensión.");
			setPrinterConfig({ SelectedPrinterName: null, SuggestedCharsPerLine: 48, IsPrinterSelected: false });
		} finally {
			setConfigLoading(false);
		}
	};

	// Function to generate return receipt data for thermal printing
	const generateReturnReceiptData = (item) => {
		return {
			receiptType: 'RETURN_RECEIPT',
			returnId: item.eventDressID,
			dressName: item.dressName,
			dressCode: item.code,
			clientName: item.clientName,
			eventTitle: item.eventTitle,
			rentalDate: item.eventDateStart,
			returnDate: item.eventDateEnd,
			actualReturnDate: new Date().toISOString(),
			paymentStatus: item.paymentStatus,
			notes: `Devolución del vestido ${item.dressName} (${item.code}) correspondiente al evento "${item.eventTitle}"`
		};
	};

	// Function to handle direct thermal print for return receipt
	const handleDirectThermalPrint = async (returnData) => {
		if (isPortrait) return messageApi.warning('La impresión térmica no está disponible en dispositivos móviles.');
		if (!returnData) {
			messageApi.error('No hay datos de devolución para imprimir.');
			return false;
		}

		// Chequear la extensión en tiempo real antes de imprimir
		const isInstalled = await checkExtensionRealTime();
		if (!isInstalled) {
			console.log('Extensión no instalada para handleDirectThermalPrint');
			return false; // No mostrar error, ya que se maneja en checkExtensionRealTime
		}

		messageApi.loading({ content: 'Generando recibo de devolución...', key: 'thermalPrint' });
		Swal.fire({
			title: 'Generando recibo de devolución...',
			text: 'Por favor espere...',
			showConfirmButton: false,
			allowOutsideClick: false,
			allowEscapeKey: false,
			didOpen: () => {
				Swal.showLoading();
			}
		});

		try {
			// Generar datos ESC/POS usando la función existente pero adaptada para recibos de devolución
			const charsPerLine = printerConfig?.SuggestedCharsPerLine || 48;
			const escposData = generateReturnReceiptESCPOS(returnData, charsPerLine);

			console.log('Enviando proxyRequest para /print via extensión');
			const response = await sendToExtension({
				action: 'proxyRequest',
				path: '/print',
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: {
					data: escposData,
					printerName: printerConfig.IsPrinterSelected ? printerConfig.SelectedPrinterName : null,
					printType: 'RAW'
				}
			});

			if (response && response.success) {
				Swal.close();
				messageApi.success({ content: `Recibo de devolución enviado: ${response.data}`, key: 'thermalPrint', duration: 3 });
				return true;
			} else {
				const errorMsg = response && response.error ? response.error : 'Respuesta inválida de la extensión';
				console.error('Error en respuesta de la extensión:', errorMsg);
				Swal.close();

				// Ofrecer impresión alternativa con navegador
				const fallbackResult = await Swal.fire({
					title: 'Impresión térmica fallida',
					text: '¿Desea imprimir el recibo usando el navegador?',
					icon: 'question',
					showCancelButton: true,
					confirmButtonText: 'Sí, imprimir con navegador',
					cancelButtonText: 'No, continuar sin imprimir',
					confirmButtonColor: '#4b0082',
					cancelButtonColor: '#d33',
				});

				if (fallbackResult.isConfirmed) {
					return await handleBrowserPrint(returnData);
				} else {
					messageApi.warning({ content: 'Recibo no impreso. La devolución fue registrada exitosamente.', key: 'thermalPrint', duration: 4 });
					return false;
				}
			}
		} catch (error) {
			console.error('Error al enviar el recibo de devolución via extensión:', error);
			Swal.close();

			// Ofrecer impresión alternativa con navegador
			const fallbackResult = await Swal.fire({
				title: 'Error de conexión con impresora',
				text: '¿Desea imprimir el recibo usando el navegador?',
				icon: 'question',
				showCancelButton: true,
				confirmButtonText: 'Sí, imprimir con navegador',
				cancelButtonText: 'No, continuar sin imprimir',
				confirmButtonColor: '#4b0082',
				cancelButtonColor: '#d33',
			});

			if (fallbackResult.isConfirmed) {
				return await handleBrowserPrint(returnData);
			} else {
				messageApi.warning({ content: 'Recibo no impreso. La devolución fue registrada exitosamente.', key: 'thermalPrint', duration: 4 });
				return false;
			}
		}
	};

	// Function to handle browser print fallback
	const handleBrowserPrint = async (returnData) => {
		try {
			messageApi.loading({ content: 'Preparando recibo para impresión...', key: 'browserPrint' });

			// Crear contenido HTML para impresión
			const printContent = generateBrowserPrintContent(returnData);

			// Crear una nueva ventana para impresión
			const printWindow = window.open('', '_blank', 'width=400,height=600');
			if (!printWindow) {
				messageApi.error({ content: 'No se pudo abrir la ventana de impresión. Verifique que su navegador permita popups.', key: 'browserPrint', duration: 5 });
				return false;
			}

			printWindow.document.write(printContent);
			printWindow.document.close();

			// Esperar a que se cargue el contenido
			printWindow.onload = () => {
				messageApi.success({ content: 'Recibo preparado. La ventana de impresión se abrirá automáticamente.', key: 'browserPrint', duration: 3 });

				// El HTML ya maneja la impresión automática, no necesitamos hacer nada más aquí
			};

			return true;
		} catch (error) {
			console.error('Error al preparar impresión del navegador:', error);
			messageApi.error({ content: 'Error al preparar el recibo para impresión.', key: 'browserPrint', duration: 5 });
			return false;
		}
	};

	// Function to generate HTML content for browser printing
	const generateBrowserPrintContent = (returnData) => {
		const currentDate = new Date(returnData.actualReturnDate).toLocaleString('es-ES', {
			dateStyle: 'short',
			timeStyle: 'short'
		});

		const rentalDate = new Date(returnData.rentalDate).toLocaleString('es-ES', {
			dateStyle: 'short',
			timeStyle: 'short'
		});

		const expectedReturnDate = new Date(returnData.returnDate).toLocaleString('es-ES', {
			dateStyle: 'short',
			timeStyle: 'short'
		});

		return `
		<!DOCTYPE html>
		<html lang="es">
		<head>
			<meta charset="UTF-8">
			<meta name="viewport" content="width=device-width, initial-scale=1.0">
			<title>Recibo de Devolución</title>
			<style>
				body {
					margin: 0;
					padding: 5px;
					font-family: 'Courier New', monospace;
					font-size: 12px;
					line-height: 1.2;
					background-color: white;
					color: black;
				}

				.receipt {
					width: 80mm;
					margin: 0 auto;
					padding: 5px;
					box-sizing: border-box;
				}

				@media print {
					body {
						margin: 0;
						padding: 0;
					}

					.receipt {
						width: 80mm;
						margin: 0;
						padding: 2px;
					}

					.no-print {
						display: none !important;
					}
				}

				.center {
					text-align: center;
					font-weight: bold;
				}

				.bold {
					font-weight: bold;
				}

				.line {
					border-bottom: 1px solid #000;
					margin: 5px 0;
				}

				.double-line {
					border-bottom: 2px solid #000;
					margin: 5px 0;
				}

				.section {
					margin: 8px 0;
				}

				.section-title {
					font-weight: bold;
					margin-bottom: 3px;
				}

				.row {
					margin: 2px 0;
				}

				.confirmation {
					text-align: center;
					font-weight: bold;
					font-size: 14px;
					margin: 10px 0;
					padding: 5px;
					border: 1px solid #000;
				}

				.footer {
					text-align: center;
					font-size: 10px;
					margin-top: 10px;
				}

				.print-btn {
					background: #4b0082;
					color: white;
					border: none;
					padding: 10px 20px;
					border-radius: 4px;
					cursor: pointer;
					font-size: 14px;
					margin: 20px auto;
					display: block;
				}

				.print-btn:hover {
					background: #3a0066;
				}
			</style>
		</head>
		<body>
			<div class="receipt">
				<div class="center">
					RECIBO DE DEVOLUCIÓN<br>
					DE VESTIDO
				</div>

				<div class="line"></div>

				<div class="center bold">
					D'Princess<br>
					Alquiler de Vestidos
				</div>

				<div class="line"></div>

				<div class="section">
					<div class="section-title">FECHA DE DEVOLUCIÓN:</div>
					<div class="row">${currentDate}</div>
				</div>

				<div class="section">
					<div class="section-title">INFORMACIÓN DEL CLIENTE:</div>
					<div class="row">Cliente: ${returnData.clientName}</div>
				</div>

				<div class="section">
					<div class="section-title">DETALLE DEL VESTIDO:</div>
					<div class="row">Vestido: ${returnData.dressName}</div>
					<div class="row">Código: ${returnData.dressCode}</div>
					<div class="row">Evento: ${returnData.eventTitle}</div>
				</div>

				<div class="section">
					<div class="section-title">PERIODO DE ALQUILER:</div>
					<div class="row">Fecha de Alquiler: ${rentalDate}</div>
					<div class="row">Fecha de Devolución Pautada: ${expectedReturnDate}</div>
				</div>

				<div class="section">
					<div class="section-title">ESTADO DEL PAGO:</div>
					<div class="row">Estado: ${returnData.paymentStatus.toUpperCase()}</div>
				</div>

				<div class="confirmation">
					VESTIDO RECIBIDO SATISFACTORIAMENTE
				</div>

				${returnData.notes ? `
				<div class="section">
					<div class="section-title">NOTAS:</div>
					<div class="row">${returnData.notes}</div>
				</div>
				` : ''}

				<div class="double-line"></div>

				<div class="center bold">
					¡GRACIAS POR PREFERIRNOS!
				</div>

				<div class="footer">
					Conserve este recibo como comprobante<br>
					de devolución del vestido.
				</div>
			</div>

			<div class="no-print" style="text-align: center; margin: 20px;">
				<button class="print-btn" onclick="window.print()">Imprimir Recibo</button>
			</div>

			<script>
				// Auto-imprimir después de un breve delay
				setTimeout(() => {
					window.print();
				}, 500);
			</script>
		</body>
		</html>
		`;
	};

	// Function to generate ESC/POS data for return receipt
	const generateReturnReceiptESCPOS = (returnData, paperWidthChars) => {
		const ESC = '\x1B';
		const GS = '\x1D';
		const FS = '\x1C';
		const CMD = {
			INIT: ESC + '@',
			BOLD_ON: ESC + 'E' + '\x01',
			BOLD_OFF: ESC + 'E' + '\x00',
			ALIGN_LEFT: ESC + 'a' + '\x00',
			ALIGN_CENTER: ESC + 'a' + '\x01',
			ALIGN_RIGHT: ESC + 'a' + '\x02',
			CUT_PAPER_FULL: GS + 'V' + '\x00',
			PRINT_NV_LOGO: (keyCode, mode = 0) => FS + 'p' + String.fromCharCode(keyCode) + String.fromCharCode(mode),
			LF: '\n',
		};

		const paperWidth = paperWidthChars || 48;
		let receipt = '';

		// Inicializar impresora
		receipt += CMD.INIT;

		// Encabezado
		receipt += CMD.ALIGN_CENTER;
		receipt += CMD.PRINT_NV_LOGO(1, 0) + CMD.LF;
		receipt += CMD.BOLD_ON;
		receipt += 'Recibo de devolución' + CMD.LF;
		receipt += 'de vestido' + CMD.LF;
		receipt += CMD.BOLD_OFF;
		receipt += CMD.LF;

		// Información del negocio
		receipt += CMD.ALIGN_CENTER;
		receipt += "D'PRINCESS STGO." + CMD.LF;
		receipt += 'Foto films' + CMD.LF;
		receipt += CMD.LF;

		// Fecha y hora de devolución
		receipt += CMD.ALIGN_LEFT;
		receipt += `Fecha de Devolución: ${new Date(returnData.actualReturnDate).toLocaleString('es-ES')}` + CMD.LF;
		receipt += CMD.LF;

		// Información del cliente
		receipt += CMD.BOLD_ON + 'INFORMACIÓN DEL CLIENTE:' + CMD.BOLD_OFF + CMD.LF;
		receipt += `Cliente: ${returnData.clientName}` + CMD.LF;
		receipt += CMD.LF;

		// Información del vestido
		receipt += CMD.BOLD_ON + 'DETALLE DEL VESTIDO:' + CMD.BOLD_OFF + CMD.LF;
		receipt += `Vestido: ${returnData.dressName}` + CMD.LF;
		receipt += `Código: ${returnData.dressCode}` + CMD.LF;
		receipt += `Evento: ${returnData.eventTitle}` + CMD.LF;
		receipt += CMD.LF;

		// Fechas del alquiler
		receipt += CMD.BOLD_ON + 'PERIODO DE ALQUILER:' + CMD.BOLD_OFF + CMD.LF;
		receipt += `Fecha de Alquiler: ${new Date(returnData.rentalDate).toLocaleString('es-ES', {
			dateStyle: 'short',
			timeStyle: 'short'
		})}` + CMD.LF;
		receipt += `Fecha de Devolución Pautada: ${new Date(returnData.returnDate).toLocaleString('es-ES', {
			dateStyle: 'short',
			timeStyle: 'short'
		})}` + CMD.LF;
		receipt += CMD.LF;

		// Estado del pago
		receipt += CMD.BOLD_ON + 'ESTADO DEL PAGO:' + CMD.BOLD_OFF + CMD.LF;
		receipt += `Estado: ${returnData.paymentStatus.toUpperCase()}` + CMD.LF;
		receipt += CMD.LF;

		// Mensaje de confirmación
		receipt += CMD.ALIGN_CENTER;
		receipt += CMD.BOLD_ON;
		receipt += 'VESTIDO RECIBIDO' + CMD.LF;
		receipt += CMD.BOLD_OFF;
		receipt += CMD.LF;

		// Notas adicionales
		if (returnData.notes) {
			receipt += CMD.ALIGN_LEFT;
			receipt += CMD.BOLD_ON + 'NOTAS:' + CMD.BOLD_OFF + CMD.LF;
			receipt += returnData.notes + CMD.LF;
			receipt += CMD.LF;
		}

		// Pie de página
		receipt += CMD.ALIGN_CENTER;
		receipt += CMD.BOLD_ON;
		receipt += '¡GRACIAS POR PREFERIRNOS!' + CMD.LF;
		receipt += CMD.BOLD_OFF;
		receipt += CMD.LF;
		receipt += 'Conserve este recibo como comprobante' + CMD.LF;
		receipt += 'de devolución del vestido.' + CMD.LF + CMD.LF;
		receipt += CMD.LF;
		receipt += CMD.LF;

		// Cortar papel
		receipt += CMD.CUT_PAPER_FULL;

		return receipt;
	};

	// Function to mark an item as received
	const handleMarkItemASReceived = async (item) => {
		const token = localStorage.getItem('token');
		try {
			const confirmation = await Swal.fire({
				title: 'Confirmar recepción',
				text: `¿Está seguro de que desea marcar el vestido ${item.dressName} como recibido?`,
				icon: 'question',
				showCancelButton: true,
				confirmButtonText: 'Sí, marcar como recibido',
				cancelButtonText: 'Cancelar',
				confirmationButtonColor: '#4b0082',
				cancelButtonColor: '#d33',
			})
			if (confirmation.isConfirmed) {
				messageApi.loading({ content: 'Marcando vestido como recibido...', key: 'updateStatus' });
				try {
					const response = await fetch(`https://dprincess-training.servehttp.com/pending-item/dress/${item.eventDressID}`, {
						method: 'PUT',
						headers: {
							'Content-Type': 'application/json',
							'Authorization': `Bearer ${token}`
						},
						body: JSON.stringify(item)
					});
					if (!response.ok) {
						let errorMessage;
						if (response.status === 404) {
							errorMessage = `Error al procesa la solicitud (Código: ${response.status}), no se encontró el recurso.`;
						}
						else if (response.status === 403) {
							errorMessage = `Error al procesar la solicitud (Código: ${response.status}), no tienes permisos para acceder a este recurso.`;
						}
						else {
							errorMessage = `Error al procesar la solicitud (Código: ${response.status}), por favor intente de nuevo.`;
						}
						console.error(`Failed request to update item ${item._id}: Status ${response.status}, StatusText: ${response.statusText}`);
						return messageApi.error({
							content: errorMessage,
							key: 'updateStatus',
							duration: 3
						})
					}
					setPendingItems((prevItems) => prevItems.filter((i) => i.eventDressID !== item.eventDressID));

					messageApi.success({
						content: 'Vestido marcado como recibido',
						duration: 2,
						key: 'updateStatus'
					});

					// Imprimir recibo térmico de devolución
					try {
						const returnData = generateReturnReceiptData(item);
						const printSuccess = await handleDirectThermalPrint(returnData);

						if (printSuccess) {
							messageApi.success({
								content: 'Vestido devuelto e impreso exitosamente',
								duration: 3
							});
						} else {
							messageApi.info({
								content: 'Vestido devuelto. El recibo no se imprimió.',
								duration: 4
							});
						}
					} catch (printError) {
						console.error('Error printing return receipt:', printError);
						// No mostrar error al usuario ya que la devolución fue exitosa
						messageApi.warning({
							content: 'Devolución registrada exitosamente. Error al imprimir recibo.',
							duration: 3
						});
					}
				} catch (error) {
					console.error("Error marking item as received:", error);
					messageApi.error({
						content: `Error al actualizar: ${error.message}`,
						key: 'updateStatus',
						duration: 3
					});
				}
			}
		} catch (error) {
			console.error("Error marking item as received:", error);
			messageApi.error({
				content: 'Error al marcar vestido como recibido, por favor intente de nuevo',
				duration: 2,
				className: 'custom-class',
				style: {
					marginTop: '20vh',
					backgroundColor: '#f0f0f0',
					color: '#000',
					fontSize: '16px',
					textAlign: 'center',
				},
			});
		}
	};

	// Function to show user error message
	const handleRetryRequest = async (request, reqName) => {
		try {
			messageApi.warning({
				content: 'Retrying request for ' + reqName + ', please wait...',
				duration: 2,
				className: 'custom-class',
				style: {
					marginTop: '20vh',
					backgroundColor: '#f0f0f0',
					color: '#000',
					fontSize: '16px',
					textAlign: 'center',
				},
			}).then(async () => await request())
		} catch (error) {
			messageApi.error({
				content: `Error retrying request for ${reqName}, please refresh the page`,
				duration: 2,
				className: 'custom-class',
				style: {
					marginTop: '20vh',
					backgroundColor: '#f0f0f0',
					color: '#000',
					fontSize: '16px',
					textAlign: 'center',
				},
			});
		}
	};

	// Function to handle search input change
	const handleSearchChange = (event) => {
		setSearch(event.target.value);
	};

	// Function to handle date range change
	const handleDateRangeChange = (dates) => {
		if (dates && dates.length > 0) {
			// Ensure start is beginning of day and end is end of day
			const rangeDates = dates.map((date) => date.format('YYYY-MM-DD HH:mm:ss'));
			const startDate = dayjs(rangeDates[0]);
			const endDate = dayjs(rangeDates[1]);
			setDateRange([startDate, endDate]);
		} else {
			setDateRange(null); // Clear the date range
		}
	};

	// Function to filter pending items based on search and date range
	const applyFilters = useCallback(() => {
		let filteredItems = [...reqResponse];

		// Apply date filter if dateRange is set
		if (dateRange && dateRange.length === 2) {
			const [startFilterDate, endFilterDate] = dateRange;

			filteredItems = filteredItems.filter((item) => {
				// Parse item dates - dayjs will parse ISO strings
				const itemDateStart = dayjs(item.eventDateStart);
				const itemDateEnd = dayjs(item.eventDateEnd);

				const startIsValid = itemDateStart.isValid();
				const endIsValid = itemDateEnd.isValid();

				// Check if the item's start OR end date falls within the filter range (inclusive)
				const isStartBetween = startIsValid && itemDateStart.isBetween(startFilterDate, endFilterDate, 'day', '[]');
				const isEndBetween = endIsValid && itemDateEnd.isBetween(startFilterDate, endFilterDate, 'day', '[]');

				const shouldInclude = isStartBetween || isEndBetween;

				return shouldInclude;
			});
		}

		// Apply search filter if search term exists
		if (search && search.trim() !== "") {
			const lowerCaseSearch = search.toLowerCase();
			filteredItems = filteredItems.filter((item) => {
				return (
					item.dressName.toLowerCase().includes(lowerCaseSearch) ||
					item.clientName.toLowerCase().includes(lowerCaseSearch) ||
					item.code.toLowerCase().includes(lowerCaseSearch)
				);
			});
		}

		// Sort filtered items by eventDateStart
		filteredItems.sort((a, b) => {
			const dateA = dayjs(a.eventDateStart); // Use dayjs for consistency
			const dateB = dayjs(b.eventDateStart);
			if (!dateA.isValid() && !dateB.isValid()) return 0;
			if (!dateA.isValid()) return 1;
			if (!dateB.isValid()) return -1;
			return dateA.valueOf() - dateB.valueOf(); // Compare timestamps
		});

		setPendingItems(filteredItems); // Update the displayed list
	}, [reqResponse, search, dateRange]);

	useEffect(() => {
		getPendingItems();
	}, []);

	useEffect(() => {
		const checkExtension = async () => {
			if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
				setExtensionInstalled(false);
				setExtensionChecked(true);
				messageApi.error('La extensión no está instalada o no se puede acceder.');
				return;
			}

			// Obtener ID dinámico de localStorage
			const extensionId = localStorage.getItem('thermalPrinterExtensionId');
			console.log('Extension ID from localStorage en useEffect:', extensionId);

			if (!extensionId) {
				setExtensionInstalled(false);
				setExtensionChecked(true);
				messageApi.error('No se encontró el ID de la extensión en localStorage. Asegúrate de que la extensión esté instalada.');
				return;
			}

			try {
				const response = await new Promise((resolve, reject) => {
					chrome.runtime.sendMessage(
						extensionId,
						{ action: 'ping' },
						(response) => {
							if (chrome.runtime.lastError) {
								reject(new Error(chrome.runtime.lastError.message));
							} else {
								resolve(response);
							}
						}
					);
				});

				setExtensionInstalled(response && response.success);
			} catch (error) {
				console.error('Error verificando extensión:', error);
				messageApi.error('Error al verificar la extensión: ' + error.message);
				setExtensionInstalled(false);
			} finally {
				setExtensionChecked(true);
			}
		};

		checkExtension();
	}, []);

	// Obtener configuración de impresora después de verificar la extensión
	useEffect(() => {
		if (extensionChecked) {
			fetchAgentPrinterConfiguration();
		}
	}, [extensionChecked]);

	// Leer parámetros de la URL al montar / cuando cambie el querystring
	useEffect(() => {
		const params = new URLSearchParams(location.search);
		let didInit = false;
		const q = params.get('search') || params.get('q');
		if (q) { setSearch(q); didInit = true; }
		const start = params.get('start') || params.get('from');
		const end = params.get('end') || params.get('to');
		if (start && end) {
			const mStart = dayjs(start);
			const mEnd = dayjs(end);
			if (mStart.isValid() && mEnd.isValid()) {
				// Asegurar orden correcto
				const ordered = mStart.isAfter(mEnd) ? [mEnd, mStart] : [mStart, mEnd];
				setDateRange(ordered);
				didInit = true;
			}
		}
		setInitializedFromURL(true);
		if (didInit) {
			// No hacemos nada más; applyFilters se disparará por efecto existente
		}
	}, [location.search]);

	// Sincronizar estado -> URL (shareable)
	useEffect(() => {
		if (!initializedFromURL) return;
		const params = new URLSearchParams(location.search);
		// Search
		if (search) params.set('search', search); else params.delete('search');
		// Date range
		if (dateRange && dateRange.length === 2) {
			params.set('start', dateRange[0].format('YYYY-MM-DD'));
			params.set('end', dateRange[1].format('YYYY-MM-DD'));
		} else {
			params.delete('start');
			params.delete('end');
		}
		const newSearch = params.toString();
		const target = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
		if (target !== `${location.pathname}${location.search}`) {
			navigate(target, { replace: true });
		}
	}, [search, dateRange, initializedFromURL]);

	useEffect(() => {
		if (reqResponse.length > 0) {
			applyFilters();
		}
		else {
			setPendingItems([]);
		}
	}, [search, dateRange, reqResponse, applyFilters]);

	// Función helper para enviar mensajes a la extensión (con ID dinámico o sin ID)
	const sendToExtension = async (message) => {
		console.log('window.thermalPrinterProxy exists:', !!window.thermalPrinterProxy);
		console.log('window.thermalPrinterProxy value:', window.thermalPrinterProxy);

		if (window.thermalPrinterProxy) {
			// Usar función global inyectada por la extensión
			console.log('Usando función global thermalPrinterProxy');
			const response = await window.thermalPrinterProxy.sendMessage(message);

			// Verificar si la respuesta es una instancia de Response (de fetch)
			if (response instanceof Response) {
				try {
					if (response.ok) {
						const data = await response.text();
						return { success: true, data };
					} else {
						const error = await response.text();
						return { success: false, error };
					}
				} catch (e) {
					console.error('Error al leer el body de la Response:', e);
					return { success: false, error: 'Error al procesar la respuesta de la extensión: ' + e.message };
				}
			} else {
				// Asumir que es un objeto con { success, data, error }
				return response;
			}
		} else {
			// Obtener ID dinámico de localStorage
			const extensionId = localStorage.getItem('thermalPrinterExtensionId');
			console.log('Extension ID from localStorage:', extensionId);

			if (extensionId) {
				console.log('Usando chrome.runtime.sendMessage con ID dinámico:', extensionId);
				return new Promise((resolve, reject) => {
					chrome.runtime.sendMessage(
						extensionId,
						message,
						(response) => {
							if (chrome.runtime.lastError) {
								reject(new Error(chrome.runtime.lastError.message));
							} else {
								resolve(response);
							}
						}
					);
				});
			} else {
				messageApi.error('No se encontró el ID de la extensión en localStorage. Asegúrate de que la extensión esté instalada.');
				return { success: false, error: 'No se encontró el ID de la extensión en localStorage.' };
			}
		}
	};

	// Función para chequear la extensión en tiempo real (actualizada)
	const checkExtensionRealTime = async () => {
		console.log('Iniciando checkExtensionRealTime...');
		if (window.thermalPrinterProxy) {
			console.log('Función global thermalPrinterProxy disponible');
			return true;
		}
		if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
			console.log('Chrome runtime no disponible');
			return false;
		}

		// Obtener ID dinámico de localStorage
		const extensionId = localStorage.getItem('thermalPrinterExtensionId');
		console.log('Extension ID from localStorage:', extensionId);

		if (!extensionId) {
			console.log('No hay ID en localStorage, extensión no disponible');
			return false;
		}

		try {
			console.log('Enviando ping a extensión con ID dinámico:', extensionId);
			const response = await new Promise((resolve, reject) => {
				chrome.runtime.sendMessage(
					extensionId,
					{ action: 'ping' },
					(response) => {
						if (chrome.runtime.lastError) {
							reject(new Error(chrome.runtime.lastError.message));
						} else {
							resolve(response);
						}
					}
				);
			});
			console.log('Respuesta de extensión al ping:', response);
			return response && response.success;
		} catch (error) {
			console.error('Error verificando extensión en tiempo real:', error);
			return false;
		}
	};

	// Cargar información de carga
	if (loading) {
		// Mostrar información de carga
		return <LoadingInformation />;
	}

	return (
		<Container sx={{
			marginTop: { xs: 1, md: 2 },
			maxWidth: { xs: '100%', md: '1200px' },
			width: '100%',
			px: { xs: 1, sm: 2, md: 3 }
		}}>
			{contextHolder}
			<Box sx={{
				width: '100%',
				minWidth: { xs: 'auto', md: '1000px' },
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				marginBottom: { xs: 2, md: 4 },
				marginTop: { xs: 1, md: 2 }
			}}>
				<Box container spacing={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
					<Grid item xs={12} sm={6} sx={{ marginBottom: { xs: 1, md: 2 } }}>
						<Card variant="outlined">
							<CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
								<Typography
									variant={isMobile ? "h6" : "h5"}
									component="div"
									sx={{
										marginBottom: { xs: 1, md: 2 },
										width: '100%',
										display: 'flex',
										alignItems: 'center',
										backgroundColor: '#531dab',
										color: '#fff',
										padding: { xs: '8px', md: '10px' },
										borderRadius: '5px',
										fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
									}}
								>
									Vestidos pendientes de devolución
								</Typography>
								<Grid container spacing={{ xs: 1, md: 2 }} sx={{ height: 'auto' }}>
									<Grid item xs={12} md={6} sx={{
										display: 'flex',
										alignItems: 'flex-start',
										mb: { xs: 2, md: 0 }
									}}>
										<TextField
											label="Buscar por cliente o vestido"
											variant="outlined"
											fullWidth
											value={search}
											onChange={(e) => handleSearchChange(e)}
											size={isMobile ? "small" : "medium"}
											sx={{ backgroundColor: '#fff' }}
										/>
									</Grid>
									<Grid item xs={12} md={6} sx={{
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'flex-start'
									}}>
										<ConfigProvider locale={esES} theme={{ token: { colorPrimary: '#4b0082' } }}>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{
													marginBottom: '8px',
													fontSize: { xs: '0.875rem', md: '1rem' }
												}}
											>
												Filtrar por fecha
											</Typography>
											<DatePicker.RangePicker
												value={dateRange}
												style={{ width: '100%' }}
												onChange={(dates) => handleDateRangeChange(dates)}
												format="YYYY-MM-DD"
												placeholder={['Fecha Inicio', 'Fecha Fin']}
												allowClear={true}
												size={isMobile ? "small" : "middle"}
											/>
										</ConfigProvider>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Card variant="outlined">
							<CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
								<Typography
									variant={isMobile ? "h6" : "h6"}
									component="div"
									sx={{
										marginBottom: { xs: 1, md: 1 },
										width: '100%',
										display: 'flex',
										alignItems: 'center',
										backgroundColor: '#f759ab',
										color: '#fff',
										padding: { xs: '8px', md: '10px' },
										borderRadius: '5px',
										fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
										fontWeight: 'bold'
									}}
								>
									Vestidos pendientes por recibir
								</Typography>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										{
											pendingItems?.length > 0 && pendingItems.map((item, index) => (
												<Card
													variant="outlined"
													key={index}
													sx={{
														marginTop: { xs: 1, md: 2 },
														fontSize: '0.8rem'
													}}
												>
													<CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
														{/* Header con nombre del vestido y botón de detalles */}
														<Grid container spacing={1} sx={{ mb: { xs: 1, md: 2 } }}>
															<Grid item xs={isMobile ? 9 : 10}>
																<Typography
																	variant={isMobile ? "subtitle1" : "h6"}
																	sx={{
																		display: 'flex',
																		alignItems: 'center',
																		gap: 1,
																		fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
																		lineHeight: 1.2,
																		flexWrap: 'wrap'
																	}}
																>
																	<CheckroomIcon
																		fontSize={isMobile ? "small" : "medium"}
																		sx={{ color: '#f759ab' }}
																	/>
																	<Box component="span" sx={{ wordBreak: 'break-word' }}>
																		Vestido {item.dressName} ({item.eventTitle})
																	</Box>
																</Typography>
															</Grid>
															<Grid item xs={isMobile ? 3 : 2} sx={{
																display: 'flex',
																justifyContent: 'flex-end',
																alignItems: 'flex-start'
															}}>
																<Tooltip title="Ver detalles" placement="top" arrow>
																	<Avatar sx={{
																		backgroundColor: 'primary.main',
																		width: { xs: 32, md: 36 },
																		height: { xs: 32, md: 36 },
																		cursor: 'pointer'
																	}}>
																		<FormatListBulletedIcon
																			fontSize={isMobile ? "medium" : "large"}
																			sx={{
																				color: '#fff',
																				fontSize: { xs: '1.25rem', md: '1.5rem' }
																			}}
																			onClick={() => navigate(`/events/view-event-details/${item.eventID}`)}
																		/>
																	</Avatar>
																</Tooltip>
															</Grid>
														</Grid>

														{/* Información en layout responsivo */}
														<Grid container spacing={{ xs: 1, md: 2 }}>
															{/* Código del vestido */}
															<Grid item xs={12} sm={6}>
																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{
																		display: 'flex',
																		alignItems: 'center',
																		gap: 0.5,
																		fontSize: { xs: '0.875rem', md: '1rem' },
																		mb: { xs: 1, sm: 0 }
																	}}
																>
																	<TagIcon fontSize="inherit" sx={{ color: 'primary.secondary' }} />
																	<Box component="span" sx={{ wordBreak: 'break-word' }}>
																		Código: {item.code}
																	</Box>
																</Typography>
															</Grid>
															{/* Cliente */}
															<Grid item xs={12} sm={6}>
																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{
																		display: 'flex',
																		alignItems: 'center',
																		gap: 0.5,
																		fontSize: { xs: '0.875rem', md: '1rem' },
																		mb: { xs: 1, sm: 0 }
																	}}
																>
																	<PersonIcon fontSize="inherit" sx={{ color: 'secondary.main' }} />
																	<Box component="span" sx={{ wordBreak: 'break-word' }}>
																		Cliente: {item.clientName}
																	</Box>
																</Typography>
															</Grid>
														</Grid>

														{/* Fechas */}
														<Grid container spacing={{ xs: 1, md: 2 }} sx={{ mt: 0.5 }}>
															{/* Fecha de alquiler */}
															<Grid item xs={12} md={6}>
																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{
																		display: 'flex',
																		alignItems: { xs: 'flex-start', sm: 'center' },
																		gap: 0.5,
																		fontSize: { xs: '0.875rem', md: '1rem' },
																		mb: { xs: 1, md: 0 },
																		flexDirection: { xs: 'column', sm: 'row' }
																	}}
																>
																	<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
																		<EventIcon fontSize="inherit" sx={{ color: 'violet' }} />
																		<span>Fecha de alquiler:</span>
																	</Box>
																	<Box sx={{
																		ml: { xs: 0, sm: 0.5 },
																		fontSize: { xs: '0.8rem', md: '0.875rem' },
																		fontWeight: 500
																	}}>
																		{new Date(item.eventDateStart).toLocaleString('es-ES', {
																			dateStyle: isMobile ? 'short' : 'long',
																			timeStyle: 'short'
																		})}
																	</Box>
																</Typography>
															</Grid>
															{/* Fecha de devolución */}
															<Grid item xs={12} md={6}>
																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{
																		display: 'flex',
																		alignItems: { xs: 'flex-start', sm: 'center' },
																		gap: 0.5,
																		fontSize: { xs: '0.875rem', md: '1rem' },
																		mb: { xs: 1, md: 0 },
																		flexDirection: { xs: 'column', sm: 'row' }
																	}}
																>
																	<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
																		<AssignmentReturnIcon fontSize="inherit" sx={{ color: 'blue' }} />
																		<span>Fecha de devolución:</span>
																	</Box>
																	<Box sx={{
																		ml: { xs: 0, sm: 0.5 },
																		fontSize: { xs: '0.8rem', md: '0.875rem' },
																		fontWeight: 500
																	}}>
																		{new Date(item.eventDateEnd).toLocaleString('es-ES', {
																			dateStyle: isMobile ? 'short' : 'long',
																			timeStyle: 'short'
																		})}
																	</Box>
																</Typography>
															</Grid>
														</Grid>

														{/* Estado de pago y inventario */}
														<Grid container spacing={{ xs: 1, md: 2 }} sx={{ mt: 0.5 }}>
															{/* Estado de pago */}
															<Grid item xs={12} sm={6}>
																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{
																		display: 'flex',
																		alignItems: 'center',
																		gap: 0.5,
																		fontSize: { xs: '0.875rem', md: '1rem' },
																		mb: { xs: 1, sm: 0 }
																	}}
																>
																	{item.paymentStatus === 'pendiente' ?
																		<MoneyOffIcon fontSize='inherit' sx={{ color: 'red' }} /> :
																		<PaidIcon fontSize="inherit" sx={{ color: 'green' }} />
																	}
																	Pago: {item.paymentStatus.toUpperCase()}
																</Typography>
															</Grid>
															{/* Estado de inventario con botón */}
															<Grid item xs={12} sm={6}>
																<Box sx={{
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'space-between',
																	gap: 1
																}}>
																	<Typography
																		variant="body2"
																		color="text.secondary"
																		sx={{
																			display: 'flex',
																			alignItems: 'center',
																			gap: 0.5,
																			fontSize: { xs: '0.875rem', md: '1rem' },
																			flex: 1
																		}}
																	>
																		<AssignmentLateIcon fontSize="inherit" sx={{ color: 'orange' }} />
																		Estado: {item.status === 'pendiente' ? 'Pendiente por recepción' : 'Recibido en recepción'}
																	</Typography>
																	{item.status === 'pendiente' && (
																		<Tooltip title="Marcar como recibido" placement="top">
																			<IconButton
																				size={isMobile ? "small" : "medium"}
																				color='success'
																				onClick={(e) => {
																					e.stopPropagation();
																					handleMarkItemASReceived(item);
																				}}
																				sx={{
																					minWidth: { xs: '32px', md: '40px' },
																					minHeight: { xs: '32px', md: '40px' }
																				}}
																			>
																				<CheckCircleOutlineIcon fontSize={isMobile ? "small" : "medium"} />
																			</IconButton>
																		</Tooltip>
																	)}
																</Box>
															</Grid>
														</Grid>
													</CardContent>
												</Card>
											))
										}
										{pendingItems?.length === 0 && (
											<Box sx={{
												textAlign: 'center',
												py: { xs: 2, md: 4 },
												color: 'text.secondary'
											}}>
												<Typography
													variant={isMobile ? "body1" : "h6"}
													sx={{
														mb: 1,
														fontSize: { xs: '1rem', md: '1.25rem' }
													}}
												>
													No hay vestidos pendientes
												</Typography>
												<Typography
													variant="body2"
													sx={{
														fontSize: { xs: '0.875rem', md: '1rem' }
													}}
												>
													Todos los vestidos han sido devueltos
												</Typography>
											</Box>
										)}
									</Grid>
								</Grid>
							</CardContent>
						</Card>
					</Grid>
				</Box>
			</Box>
		</Container>
	)
}

export default DressReturnProcessor;